import { LoadingContainer } from 'app/components/LoadingContainer';
import { StyledStack } from 'app/components/Stack';
import StyledButton from 'app/components/StyledButton';
import StyledCollapse, {
  CollapsePanelWrapper,
} from 'app/components/StyledCollapse';
import StyledTable from 'app/components/StyledTable';
import useGetApiData from 'app/hooks/useGetApiData';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import DialogWarning from 'app/pages/Catalog/components/DialogWarning';
import Countdown from 'app/pages/TicketRefund/components/Countdown';
import { upgradeTicketApi } from 'app/services/checkTicketApi';
import { getDetailOrderApi } from 'app/services/checkoutApi';
import * as _ from 'lodash';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { formatNumber, formatTimeFrame, getFromDateToDate } from 'utils/helper';
import useCountDown from 'utils/hooks/useCountDown';
import { Layout } from '../../layouts';
import { ACCOUNT_MNG_MENU } from '../../layouts/AccountManager';
import { CommonText, StyledDivider } from '../styled';
import useDebounce from 'app/hooks/useDebounce';
import { getPriceCartApi } from 'app/services/showApi';
import { EXCHANGE_TICKETS_SELECTED, TICKET_TYPES } from 'utils/constants';
import { checkIsApiOrder } from '../SelectChangeTicket';

const UpgradeSummary = () => {
  const { id }: { id: string } = useParams();
  const translations = useSelector(selectTranslations);
  const [messageWarning, setMessageWarning] = useState<string>('');
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { handleDebounce } = useDebounce();
  const { data, isLoading } = useGetApiData({
    api: getDetailOrderApi,
    params: id,
  });

  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const bpAllocatedDebt = data?.businessPartnerAllocatedDebt?.id;

  const { isLoading: isLoadingUpgrade, handleRequest: handleRequestUpgrade } =
    useGetApiData({
      api: upgradeTicketApi,
      isSkip: true,
    });

  const history = useHistory();
  const location = useLocation();

  const historyState: any = location?.state;

  const modalError = (contentModal: any, actionClose?: any) => {
    return onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: contentModal?.message ?? contentModal ?? '',
      closable: true,
      maskClosable: false,
      onCloseDialog: () => {
        onCloseDialogNoti();
        actionClose && actionClose();
      },
    });
  };

  const reCheckGetPrice = useCallback(
    async selectedTicket => {
      if (selectedTicket.length > 0) {
        const formData = new FormData();
        for (let index = 0; index < selectedTicket.length; index++) {
          const selectedData = selectedTicket[index];

          const params: {
            date: string;
            show_id: string;
            payment: string;
            zone_id?: string;
            used_area2?: string;
            restaurant_id?: string;
            amount?: string;
          } = {
            date: selectedData.date,
            show_id: selectedData.show_id.toString(),
            payment: selectedData.payment.toString(),
            amount: selectedData.amount,
          };
          if (selectedData.materialType !== '9005') {
            if (selectedData.zone_id) {
              params.zone_id = selectedData.zone_id.toString();
            }
            if (selectedData.restaurant_id) {
              params.restaurant_id = selectedData.restaurant_id.toString();
            }
            params.used_area2 = selectedData.used_area2.toString();
          }
          Object.entries(params).forEach(([key, value]) => {
            formData.append(`items[${index}][${key}]`, value);
          });
          formData.set(
            `items[${index}][payment]`,
            params?.payment
              ? params?.payment
              : historyState?.payment_object?.value,
          );
          if (bpAllocatedDebt !== null && bpAllocatedDebt !== undefined)
            formData.append(
              `items[${index}][bpAllocatedDebt]`,
              bpAllocatedDebt,
            );
        }
        formData.set('validate_payment', '1');
        formData.append('notValidateAmount', '1');
        formData.append('forceTicketType', !!data?.isLongTerm ? '1' : '0');
        if (checkIsApiOrder(data?.originalOrderSource))
          formData.append('numberChannel', '50');
        const res = await getPriceCartApi(formData);

        if (res.status === 200) {
          const resData = res.data;

          // compare with new inventory
          const isValidInventory: boolean = resData.every(
            (ii: any, jd: number) => {
              return (
                +ii?.inventory > 0 &&
                _.isEmpty(ii?.error?.message) &&
                +selectedTicket[jd].amount <= +ii.inventory
              );
            },
          );
          // compare with new price
          let isValidPrice: boolean = resData.every((ii: any, jd: number) => {
            return (
              +ii?.price === +selectedTicket[jd].price &&
              _.isEmpty(ii?.error?.message) &&
              +ii?.price > 0
            );
          });
          if (!isValidPrice) {
            const findError = resData.find(
              (ii: any) => !_.isEmpty(ii?.error?.message),
            );
            modalError(
              findError
                ? findError?.error?.message
                : translations?.['b2b.txt.error-price-exchange'],
              () => {
                sessionStorage.removeItem(EXCHANGE_TICKETS_SELECTED);
                onCloseDialogNoti();
                history.push(`/check-tickets/select-tickets/${id}`);
              },
            );
            return false;
          }
          if (!isValidInventory) {
            const findError = resData.find(
              (ii: any) => !_.isEmpty(ii?.error?.message),
            );
            modalError(
              findError
                ? findError?.error?.message
                : translations
                ? translations?.['b2b.txt.error-inventory-exchange']
                : '',
              () => {
                sessionStorage.removeItem(EXCHANGE_TICKETS_SELECTED);
                onCloseDialogNoti();
                history.push(`/check-tickets/select-tickets/${id}`);
              },
            );
            return false;
          }
          return true;
        }
      }
    },
    [
      historyState,
      history,
      translations,
      id,
      bpAllocatedDebt,
      data?.isLongTerm,
      data?.originalOrderSource,
    ],
  );

  const createOrder = useCallback(async () => {
    if (!executeRecaptcha) return;
    try {
      setIsCreatingOrder(true);
      const tokenCaptcha = await executeRecaptcha();
      const formData = new FormData();
      formData.append('captcha', tokenCaptcha);
      formData.append(
        'payment_method',
        `${historyState?.payment_object?.value}`,
      );
      formData.append(
        'deliver_ticket_id',
        `${historyState?.delivery_ticket_object?.value}`,
      );
      formData.append('hashId', historyState?.hashId);
      const paramSubmit = historyState?.selectedData?.selectedTicket.map(ii => {
        return {
          show_id: ii.id,
          amount: ii.amount,
          date: ii.date,
          zone_id: ii?.zone_object?.id,
          restaurant_id: ii?.restaurant_object?.id,
          used_area2: ii?.used_area2,
        };
      });
      // re-check price
      const resultCheck = await reCheckGetPrice(
        historyState?.selectedData?.selectedTicket,
      );
      if (!resultCheck) return;
      paramSubmit.forEach((item: any, index: number) => {
        if (item?.show_id)
          formData.append(`newItems[${index}][show_id]`, item?.show_id);
        if (item?.amount)
          formData.append(`newItems[${index}][amount]`, item?.amount);
        if (item?.used_area2)
          formData.append(`newItems[${index}][used_area2]`, item?.used_area2);
        if (item?.date) formData.append(`newItems[${index}][date]`, item?.date);
        if (item?.zone_id)
          formData.append(`newItems[${index}][zone_id]`, item?.zone_id);
        if (item?.restaurant_id)
          formData.append(
            `newItems[${index}][restaurant_id]`,
            item?.restaurant_id,
          );
      });
      formData.append('forceTicketType', !!data?.isLongTerm ? '1' : '0');
      handleRequestUpgrade(formData).then(res => {
        if (res?.error) {
          setMessageWarning(res?.message);
        } else if (!res?.paymentSuccess) {
          history.push(
            '/check-tickets/confirm-policy-order/' + historyState?.hashId,
            {
              menu: ACCOUNT_MNG_MENU.CHECK_TICKET,
            },
          );
        } else {
          if (
            res?.payment?.code &&
            res?.payment?.code.toLowerCase() === 'banktransfer'
          ) {
            history.push(
              '/check-tickets/confirm-policy-order/' + historyState?.hashId,
              {
                menu: ACCOUNT_MNG_MENU.CHECK_TICKET,
              },
            );
          } else {
            history.push(
              '/payment-callback?vnp_TxnRef=' + historyState?.hashId,
            );
          }
        }
      });
    } catch (error: any) {
      const errMsg = error?.message;
      if (!!errMsg)
        onOpenDialogNoti({
          type: 'ERROR',
          isShow: true,
          title: '',
          content: errMsg,
          closable: true,
          maskClosable: false,
          onCloseDialog: () => {
            onCloseDialogNoti();
          },
        });
    } finally {
      setIsCreatingOrder(false);
    }
  }, [
    executeRecaptcha,
    handleRequestUpgrade,
    history,
    historyState?.delivery_ticket_object?.value,
    historyState?.hashId,
    historyState?.payment_object?.value,
    historyState?.selectedData?.selectedTicket,
    onCloseDialogNoti,
    onOpenDialogNoti,
    reCheckGetPrice,
    data?.isLongTerm,
  ]);

  useLayoutEffect(() => {
    if (
      !historyState?.selectedData?.selectedTicket ||
      historyState?.selectedData?.selectedTicket.length < 1
    ) {
      history.push(`/check-tickets/select-tickets/${id}`);
    }
  }, [history, historyState?.selectedData?.selectedTicket, id]);

  const timeDistance = useMemo(() => {
    return (
      moment(data?.expiredAt?.datetimeDMYHIS, 'HH:mm:ss DD/MM/YYYY').valueOf() -
      moment(data?.startTime?.datetimeDMYHIS, 'HH:mm:ss DD/MM/YYYY').valueOf()
    );
  }, [data?.expiredAt?.datetimeDMYHIS, data?.startTime?.datetimeDMYHIS]);

  const { timeRemaining, resetCountDown } = useCountDown({
    time: timeDistance,
    onTimeout: () => {
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: '',
        content:
          translations?.['b2b.txt.refundOrderExpried']?.replace(
            '{orderNumber}',
            data?.id,
          ) ?? '',
        closable: true,
        maskClosable: false,
        onCloseDialog: () => {
          onCloseDialogNoti();
          history.push('/account', {
            targetTab: 'tab-3',
          });
        },
      });
    },
  });

  useEffect(() => {
    resetCountDown(timeDistance);
  }, [resetCountDown, timeDistance]);

  useEffect(() => {
    if (data?.orderState?.value === 'paymentPending') {
      history.push({
        pathname: '/account',
        state: {
          orderState: 'cancelled',
        },
      });
    }
  }, [data?.orderState?.value, history]);

  const onCloseDialogWarning = () => {
    setMessageWarning('');
  };

  const columns = [
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-name']}
        </div>
      ),
      dataIndex: 'product',
      key: 'productName',
      render: value => <div style={{ textAlign: 'left' }}>{value?.name}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.park']}
        </div>
      ),
      dataIndex: 'park',
      key: 'park',
      render: value => <div style={{ textAlign: 'left' }}>{value?.name}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-type']}
        </div>
      ),
      dataIndex: 'catalog',
      key: 'catalog',
      render: value => <div style={{ textAlign: 'left' }}>{value?.name}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.valid-from']}
        </div>
      ),
      key: 'fromDate',
      render: value => {
        const { fromDate } = getFromDateToDate({
          ticket: value?.tickets?.[0],
        });
        return <div style={{ textAlign: 'left' }}>{fromDate && fromDate}</div>;
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.valid-to']}
        </div>
      ),
      key: 'toDate',
      render: value => {
        const { toDate } = getFromDateToDate({
          ticket: value?.tickets?.[0],
        });
        return <div style={{ textAlign: 'left' }}>{toDate && toDate}</div>;
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-detail']}
        </div>
      ),
      key: 'detail',
      render: value => {
        const isCableTicket = value?.materialType === TICKET_TYPES.CABLE_CAR;
        const zone = value?.zone?.name;
        const restaurant = value?.restaurant?.name;
        const performance = formatTimeFrame(
          value?.product?.usedArea2Object?.label,
        );

        if (isCableTicket) return <div />;

        return (
          <div style={{ textAlign: 'left' }}>
            {zone && <div>{zone}</div>}
            {restaurant && <div>{restaurant}</div>}
            {performance && <div>{performance}</div>}
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-amount']}
        </div>
      ),
      dataIndex: 'amount',
      key: 'amount',
      render: value => <div style={{ textAlign: 'left' }}>{value}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.bill']}
        </div>
      ),
      key: 'originalPrice',
      render: value => (
        <div
          style={{ textAlign: 'left', display: !value?.id ? 'none' : 'flex' }}
        >
          {`${formatNumber(value?.originalPrice).replaceAll(',', '.')}đ`}
        </div>
      ),
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.total-bill']}
        </div>
      ),
      key: 'price',
      render: value => (
        <div
          style={{ textAlign: 'left', color: !value?.id ? '#E92127' : 'black' }}
        >
          {`${formatNumber(value?.price).replaceAll(',', '.')}đ`}
        </div>
      ),
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.refun-percent']}
        </div>
      ),
      dataIndex: 'exchangeRate',
      key: 'exchangeRate',
      render: value => {
        if (value === undefined) return;
        return <div style={{ textAlign: 'left' }}>{Number(value) + '%'}</div>;
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.refund-fee']}
        </div>
      ),
      key: 'exchangeCharge',
      render: value => {
        return (
          <div
            style={{
              textAlign: 'left',
              color: !value?.id ? '#E92127' : 'black',
            }}
          >
            {`${formatNumber(Number(value?.exchangeCharge || 0)).replaceAll(
              ',',
              '.',
            )}đ`}
          </div>
        );
      },
    },
  ];

  const column2 = [
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-name']}
        </div>
      ),
      dataIndex: 'childName',
      key: 'childName',
      render: value => <div style={{ textAlign: 'left' }}>{value}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.park']}
        </div>
      ),
      dataIndex: 'park',
      key: 'park',
      render: value => <div style={{ textAlign: 'left' }}>{value?.name}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-type']}
        </div>
      ),
      dataIndex: 'catalog',
      key: 'catalog',
      render: value => <div style={{ textAlign: 'left' }}>{value?.name}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.exp-date']}
        </div>
      ),
      dataIndex: 'dateSelectFrom',
      key: 'dateFrom',
      render: value => <div style={{ textAlign: 'left' }}>{value}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-detail']}
        </div>
      ),
      key: 'detail',
      render: value => {
        const zone = value?.zone_object?.name;
        const performance = formatTimeFrame(value?.used_area2);
        const restaurant = value?.restaurant_object?.name;
        return (
          <div style={{ textAlign: 'left' }}>
            {zone && <div>{zone}</div>}
            {restaurant && <div>{restaurant}</div>}
            {performance && <div>{performance}</div>}
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.ticket-amount']}
        </div>
      ),
      dataIndex: 'amount',
      key: 'amount',
      render: value => <div style={{ textAlign: 'left' }}>{value}</div>,
    },
    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.bill']}
        </div>
      ),
      key: 'originalPrice',
      render: value => (
        <div
          style={{ textAlign: 'left', display: !value?.id ? 'none' : 'flex' }}
        >
          {`${formatNumber(value?.price).replaceAll(',', '.')}đ`}
        </div>
      ),
    },

    {
      title: (
        <div style={{ textAlign: 'left' }}>
          {translations?.['b2b.upgrade.total-bill']}
        </div>
      ),
      key: 'price',
      render: value => {
        if (value?.totalNewTicket) {
          return (
            <div
              style={{
                textAlign: 'left',
                color: '#E92127',
              }}
            >
              {`${formatNumber(value?.totalNewTicket).replaceAll(',', '.')}đ`}
            </div>
          );
        }
        return (
          <div
            style={{
              textAlign: 'left',
              color: !value?.id ? '#E92127' : 'black',
            }}
          >
            {`${formatNumber(value?.price * value?.amount).replaceAll(
              ',',
              '.',
            )}đ`}
          </div>
        );
      },
    },
  ];

  const dataSourceFormatted = useCallback(
    listTicket => {
      const total = listTicket?.reduce(
        (obj, ticket) => {
          obj.price += Number(ticket?.price || 0);
          obj.amount += Number(ticket?.amount || 0);
          obj.exchangeCharge += Number(ticket?.exchangeCharge || 0);
          return obj;
        },
        {
          price: 0,
          amount: 0,
          exchangeCharge: 0,
          name: translations?.['b2b.upgrade.total'],
          product: { name: translations?.['b2b.upgrade.total'] },
          totalNewTicket: historyState?.selectedData?.amountCurrent,
        },
      );
      return [...(listTicket || []), total];
    },
    [historyState?.selectedData?.amountCurrent, translations],
  );

  return (
    <Layout menuActive={ACCOUNT_MNG_MENU.CHECK_TICKET}>
      <LoadingContainer
        loading={isLoading || isLoadingUpgrade || isCreatingOrder}
      >
        <StyledStack direction={'column'} rowGap={'24px'}>
          <StyledStack justifyContent={'space-between'}>
            <CommonText
              style={{
                fontSize: 20,
                color: '#000',
                fontWeight: 600,
              }}
            >
              {translations?.['b2b.upgrade.title']}
            </CommonText>
            <StyledStack
              display={{ lg: 'flex', xs: 'none' }}
              style={{ width: 'fit-content' }}
            >
              {_.isInteger(timeRemaining) && <Countdown time={timeRemaining} />}
            </StyledStack>
          </StyledStack>
          <CommonText style={{ fontStyle: 'italic', fontWeight: 400 }}>
            {`* ` +
              translations?.['b2b.upgrade.summary-note']?.replace(
                '{code}',
                data?.orderIdChanged,
              )}
          </CommonText>
          <StyledStack display={{ lg: 'none', xs: 'flex' }}>
            {timeRemaining && <Countdown time={timeRemaining} />}
          </StyledStack>
          <StyledStack>
            <StyledCollapse defaultActiveKey={['1', '2']}>
              <CollapsePanelWrapper
                key="1"
                header={translations?.['b2b.upgrade.list-previous-order']}
              >
                <StyledTable
                  dataSource={dataSourceFormatted(data?.blockItems)}
                  columns={columns}
                  styleSlots={{
                    footerCSS: {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                    },
                  }}
                  pagination={false}
                />
              </CollapsePanelWrapper>
              <CollapsePanelWrapper
                key="2"
                header={translations?.['b2b.upgrade.list-new-ticket']}
              >
                <StyledTable
                  dataSource={dataSourceFormatted(
                    historyState?.selectedData?.selectedTicket,
                  )}
                  columns={column2}
                  styleSlots={{
                    footerCSS: {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                    },
                  }}
                  pagination={false}
                />
              </CollapsePanelWrapper>
            </StyledCollapse>
          </StyledStack>
          <StyledStack direction={{ lg: 'row', xs: 'column' }} rowGap={'16px'}>
            <StyledStack direction={'column'} rowGap={'16px'}>
              <CommonText style={{ fontSize: 16, fontWeight: '600' }}>
                {translations?.['b2b.upgrade.pay-exchange-order']}
              </CommonText>
              <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
                <CommonText>
                  {translations?.['b2b.upgrade.refund-amount']}
                </CommonText>
                <CommonText style={{ color: '#E92227' }}>
                  {formatNumber(
                    historyState?.selectedData?.amountAvailable,
                  ).replaceAll(',', '.') + 'đ'}
                </CommonText>
              </StyledStack>
              <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
                <CommonText>
                  {translations?.['b2b.upgrade.total-new-order']}
                </CommonText>
                <CommonText style={{ color: '#E92227' }}>
                  {formatNumber(
                    historyState?.selectedData?.amountCurrent,
                  ).replaceAll(',', '.') + 'đ'}
                </CommonText>
              </StyledStack>
              <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
                <CommonText style={{ fontSize: 16 }}>
                  {translations?.['b2b.upgrade.total-diff']}
                </CommonText>
                <CommonText style={{ fontSize: 16, color: '#E92227' }}>
                  {formatNumber(
                    historyState?.selectedData?.amountDiff,
                  ).replaceAll(',', '.') + 'đ'}
                </CommonText>
              </StyledStack>
            </StyledStack>
          </StyledStack>
          <StyledDivider />
          <StyledStack direction={'column'} rowGap={'16px'}>
            <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
              <CommonText style={{ fontWeight: 400 }}>
                {translations?.['b2b.txt.paymentMethod']}
              </CommonText>
              <CommonText style={{ fontWeight: 400 }}>
                {' '}
                {historyState?.payment_object?.label}
              </CommonText>
            </StyledStack>
            <StyledStack columnGap={'20px'} justifyContent={'space-between'}>
              <CommonText style={{ fontWeight: 400 }}>
                {translations?.['b2b.txt.ticket-form']}
              </CommonText>
              <CommonText style={{ fontWeight: 400 }}>
                {historyState?.delivery_ticket_object?.label}
              </CommonText>
            </StyledStack>
          </StyledStack>

          <StyledDivider />
          <StyledStack justifyContent={'center'} columnGap={'16px'}>
            <StyledButton
              onClick={() => history.goBack()}
              style={{
                border: '1px solid #808080',
                color: '#808080',
                background: 'transparent',
              }}
            >
              {translations?.['b2b.txt.back']}
            </StyledButton>
            <StyledButton
              onClick={() => {
                handleDebounce(createOrder);
              }}
              style={{
                color: 'white',
                backgroundImage: 'linear-gradient(to right, #f06f24, #F8B117)',
              }}
            >
              {translations?.['b2c.txt.continue']}
            </StyledButton>
          </StyledStack>
        </StyledStack>
      </LoadingContainer>
      {!!messageWarning && (
        <DialogWarning
          open={!!messageWarning}
          onClose={onCloseDialogWarning}
          message={messageWarning}
        />
      )}
    </Layout>
  );
};

export default UpgradeSummary;
